export type GaWordReplacement = { from: string; to: string }

/**
 * 按后台配的词表替换文案。
 *
 * 替换在服务端渲染时完成，所有访客拿到的是同一份页面 —— 不是给审核一套、
 * 给用户另一套。不做前端替换也是同一个原因：审核读的是源码，前端改了源码里还是原词。
 *
 * from 为空的条目直接跳过：空字符串会被 split 成逐字符，把整段文本炸开，
 * 而且是替换之后才发作，保存那一刻看不出来。后端也挡了一次，这里是第二道。
 */
export function applyWordReplacements(text: string, pairs?: GaWordReplacement[] | null): string {
  if (!text || !pairs?.length) return text
  return pairs.reduce((acc, pair) => {
    // 不 trim：前后空格是有意义的（"Win " 连空格一起删掉）。
    // 但整条全是空白的要跳过 —— 那会把文本里每段空白都替换掉。
    const from = pair?.from || ''
    if (!from.trim()) return acc
    return acc.split(from).join(pair?.to ?? '')
  }, text)
}

/**
 * 把一份文案字典里的所有字符串都过一遍替换，返回新的一份。
 *
 * 这样做是为了覆盖「页面上任意一处文字」 —— 逐个把 JSX 里的字符串包起来，
 * 总会漏（实测就漏了 Tickets / Winner / DRAW TODAY 那几处，它们在块内部）。
 * 从字典这一层replace，页面、导航、页脚只要是读字典的地方全都覆盖到。
 *
 * **不能改成渲染完再替换整段 HTML**：那会连属性、链接和 __NEXT_DATA__ 一起改掉，
 * 而且客户端 hydration 会拿原始 props 再渲染一遍，把文字又翻回去 —— 用户会看到它闪一下变回原词。
 * 从数据这一层改，服务端和客户端渲染出来的是同一份，不存在这个问题。
 */
export function deepReplaceStrings<T>(value: T, pairs?: GaWordReplacement[] | null): T {
  if (!pairs?.length) return value
  if (typeof value === 'string') return applyWordReplacements(value, pairs) as unknown as T
  if (Array.isArray(value)) return value.map(v => deepReplaceStrings(v, pairs)) as unknown as T
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {}
    for (const key of Object.keys(value as Record<string, unknown>)) {
      out[key] = deepReplaceStrings((value as Record<string, unknown>)[key], pairs)
    }
    return out as unknown as T
  }
  return value
}
